import { Building2, Clock3, MapPin, Plus } from 'lucide-react';
import { Property } from '../types';
import { formatCurrency0 } from '../utils/format';

interface PropertyDashboardHomeProps {
  properties: Property[];
  onOpenProperty: (id: string) => void;
  onNewProperty: () => void;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
}

const PropertyDashboardHome = (props: PropertyDashboardHomeProps) => {
  const { properties, onOpenProperty, onNewProperty, emptyStateTitle, emptyStateDescription } = props;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">Property Dashboard</p>
          <h1 className="mt-2 text-[2rem] font-semibold tracking-tight text-slate-900">
            Your properties
          </h1>
          <p className="mt-3 max-w-2xl text-[15px] leading-7 text-slate-500">
            Open a saved property to continue underwriting, or start a new one in the existing creation flow.
          </p>
        </div>
        <button
          onClick={onNewProperty}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700"
        >
          <Plus size={15} /> New Property
        </button>
      </div>

      {properties.length === 0 ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-600">
            <Building2 size={26} />
          </div>
          <h2 className="mt-6 text-2xl font-semibold text-slate-900">{emptyStateTitle || 'No saved properties yet'}</h2>
          <p className="mx-auto mt-3 max-w-xl text-[15px] leading-7 text-slate-500">
            {emptyStateDescription || 'Create your first property and you\'ll land in the existing editor immediately. Once it\'s saved, it will appear here for quick access.'}
          </p>
          <button
            onClick={onNewProperty}
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-900 transition-colors hover:bg-slate-50"
          >
            <Plus size={15} /> Create First Property
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2 2xl:grid-cols-3">
          {properties.map((property) => {
            const timestamp = property.updatedAt || property.createdAt;
            return (
              <button
                key={property.id}
                onClick={() => onOpenProperty(property.id)}
                className="rounded-3xl border border-slate-200 bg-white p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Saved Property</p>
                    <h2 className="mt-3 text-[1.7rem] font-semibold tracking-tight text-slate-900">
                      {property.name || 'Untitled Property'}
                    </h2>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">
                    Open
                  </div>
                </div>

                <div className="mt-5 flex items-center gap-2 text-[15px] text-slate-500">
                  <MapPin size={14} className="text-slate-400" />
                  <span>{property.address || 'Location not set'}</span>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm font-medium text-slate-500">Purchase Price</p>
                    <p className="mt-2 text-xl font-semibold text-slate-900">{formatCurrency0(property.currentYearInputs.purchasePrice)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm font-medium text-slate-500">Annual Rent</p>
                    <p className="mt-2 text-xl font-semibold text-slate-900">{formatCurrency0(property.currentYearInputs.annualRent)}</p>
                  </div>
                </div>

                <div className="mt-5 flex items-center gap-2 text-sm text-slate-500">
                  <Clock3 size={13} />
                  <span>{timestamp ? `Saved ${new Date(timestamp).toLocaleString()}` : 'Saved time unavailable'}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PropertyDashboardHome;
